import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import BrandLogo from "@/components/BrandLogo";
import { isValidMobile, MOBILE_ERROR } from "@/lib/validation";

export default function CustomerRegister() {
  const { setCustomerAuth, customerToken } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    whatsapp: "",
  });
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState(null); // "email" | "mobile" | null

  useEffect(() => {
    if (customerToken) navigate(from, { replace: true });
  }, [customerToken, navigate, from]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (conflict && (k === "email" || k === "mobile")) setConflict(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.mobile.trim() ||
      !form.password
    ) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!isValidMobile(form.mobile)) {
      toast.error(MOBILE_ERROR);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer-register", {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
        password: form.password,
        whatsapp: form.whatsapp.trim() || null,
      });
      setCustomerAuth(data.access_token, data.customer);
      toast.success("Welcome to Tezz Khana!");
      navigate(from);
    } catch (err) {
      const detail = err?.response?.status === 409 ? errMsg(err, "") : "";
      if (detail.toLowerCase().startsWith("email already")) {
        setConflict("email");
      } else if (detail.toLowerCase().startsWith("mobile already")) {
        setConflict("mobile");
      } else {
        toast.error(errMsg(err, "Registration failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-xl">
        <BrandLogo />
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-secondary">
          Create account
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track your quotes, bookings and event details in one place.
        </p>

        <form
          onSubmit={submit}
          className="mt-8 space-y-4"
          data-testid="customer-register-form"
        >
          <Field label="Full name" required>
            <input
              data-testid="customer-register-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              className="ck-input"
            />
          </Field>
          <Field label="Email" required>
            <input
              type="email"
              data-testid="customer-register-email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              autoComplete="email"
              className="ck-input"
            />
          </Field>
          <Field label="Mobile" required>
            <input
              type="tel"
              data-testid="customer-register-mobile"
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value)}
              required
              inputMode="numeric"
              maxLength={10}
              placeholder="9812345678"
              className="ck-input"
            />
            {form.mobile.trim() !== "" && !isValidMobile(form.mobile) && (
              <span data-testid="register-mobile-error" className="mt-1 block text-xs font-medium text-destructive">
                {MOBILE_ERROR}
              </span>
            )}
          </Field>
          <Field label="WhatsApp (optional)">
            <input
              type="tel"
              data-testid="customer-register-whatsapp"
              value={form.whatsapp}
              onChange={(e) => set("whatsapp", e.target.value)}
              className="ck-input"
            />
          </Field>
          <Field label="Password" required>
            <input
              type="password"
              data-testid="customer-register-password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              autoComplete="new-password"
              className="ck-input"
            />
            <span className="mt-1 block text-[10px] text-muted-foreground">
              At least 8 characters.
            </span>
          </Field>

          {conflict && (
            <div
              data-testid={`register-conflict-${conflict}`}
              className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
            >
              <p className="font-semibold">
                {conflict === "email"
                  ? "This email is already registered — sign in instead"
                  : "This mobile number is already registered — sign in instead"}
              </p>
              <Link
                to="/account/login"
                state={{ ...(location.state || {}), prefill: conflict === "email" ? form.email.trim() : form.mobile.trim() }}
                data-testid="register-conflict-signin-btn"
                className="btn-primary mt-3 inline-flex !py-2 !text-xs"
              >
                Sign in with this {conflict === "email" ? "email" : "number"}
              </Link>
              <p className="mt-3 text-xs">
                Forgot your password?{" "}
                <Link
                  to="/account/forgot-password"
                  state={{ prefillEmail: conflict === "email" ? form.email.trim() : "" }}
                  data-testid="register-conflict-forgot-link"
                  className="font-semibold underline"
                >
                  Reset it by email
                </Link>
                .
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="customer-register-submit"
            className="btn-primary w-full"
          >
            <UserPlus className="h-4 w-4" />
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/account/login"
            state={location.state}
            data-testid="customer-register-login-link"
            className="font-semibold text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
      <style>{`.ck-input { width:100%; border-radius:0.75rem; border:1px solid hsl(var(--border)); background:hsl(var(--background)); padding:0.75rem 1rem; font-size:0.875rem; }
      .ck-input:focus { outline:none; border-color:hsl(var(--primary)); box-shadow:0 0 0 3px hsl(var(--primary) / 0.15); }`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </span>
      {children}
    </label>
  );
}
